import { PageHero } from "../components/PageHero";
import { Section, Card } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { ContactForm } from "../components/ContactForm";
import { site } from "../data/site";
import { PRODUCTS } from "../data/products";

/**
 * /contact
 *
 * The "what happens next" column is doing real work here. The most common
 * reason a good enquiry never gets sent is uncertainty about what it
 * triggers — whether it starts a sales sequence, how long a reply takes,
 * whether a person reads it. Answering that beside the form costs nothing
 * and removes the hesitation.
 */

const NEXT = [
  {
    n: "01",
    t: "A person reads it",
    b: "Not a sequence, not a chatbot, not a junior qualifying you against a script. The reply comes from someone who could build the thing you are asking about.",
  },
  {
    n: "02",
    t: "You hear back within one working day",
    b: "Usually sooner. If your enquiry needs thought, we say so and tell you when to expect the real answer rather than sending a holding note.",
  },
  {
    n: "03",
    t: "A 30-minute call, if it looks like a fit",
    b: "No slides. We ask what the problem costs you today and what would count as solved, and we tell you honestly whether we are the right people.",
  },
];

export function Contact() {
  return (
    <>
      <PageHero
        eyebrow="Start a project"
        title={
          <>
            Tell us what you are trying to{" "}
            <span className="text-gradient">build, automate or improve.</span>
          </>
        }
        lede="You do not need a specification, a budget or a decision. A description of the problem in your own words is enough to have a useful first conversation."
      />

      <Section tight>
        <div className="container">
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-10">
            <Reveal>
              <Card className="p-5 sm:p-7">
                <ContactForm />
              </Card>
            </Reveal>

            <div className="space-y-4">
              <Reveal delay={0.08}>
                <Card className="p-6 sm:p-7">
                  <h2 className="font-display text-base font-semibold tracking-tight">
                    What happens next
                  </h2>
                  <ol className="mt-5 space-y-5">
                    {NEXT.map((s) => (
                      <li key={s.n} className="flex gap-4">
                        <span className="font-mono text-[11px] tabular-nums text-white/25">
                          {s.n}
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-white">{s.t}</div>
                          <p className="mt-1.5 text-sm leading-relaxed text-white/60">
                            {s.b}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </Card>
              </Reveal>

              <Reveal delay={0.14}>
                <Card className="p-6 sm:p-7">
                  <h2 className="font-display text-base font-semibold tracking-tight">
                    Direct
                  </h2>
                  <div className="mt-4 space-y-3 text-sm">
                    <Row label="Email">
                      <a
                        href={`mailto:${site.email}`}
                        className="text-white transition-colors hover:text-emerald-300"
                      >
                        {site.email}
                      </a>
                    </Row>
                    <Row label="LinkedIn">
                      <a
                        href={site.social.linkedin}
                        target="_blank"
                        rel="noreferrer"
                        className="text-white transition-colors hover:text-emerald-300"
                      >
                        /company/astalabista
                      </a>
                    </Row>
                    <Row label="Presence">
                      <span className="text-white/75">
                        {site.locations.join(" · ")}
                      </span>
                    </Row>
                  </div>

                  <div className="mt-6 border-t border-white/[0.08] pt-5">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/35">
                      Already using a product?
                    </div>
                    <div className="mt-3 space-y-2">
                      {PRODUCTS.filter((p) => p.appHref).map((p) => (
                        <a
                          key={p.slug}
                          href={p.appHref}
                          target="_blank"
                          rel="noreferrer"
                          className="block text-sm text-white/70 transition-colors hover:text-white"
                        >
                          Sign in to {p.name} →
                        </a>
                      ))}
                    </div>
                  </div>
                </Card>
              </Reveal>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/35">
        {label}
      </span>
      <span className="text-right">{children}</span>
    </div>
  );
}
